import React, { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import GenericModal from "@ui-components/Modal/GenericModal";
import { GenericButton } from "@ui-components/Button/GenericButton";
import StatusBadge from "@ui-components/Tag/StatusBadge";
import { SkeletonTableRows } from "@ui-components/Skeleton/GenericSkeleton";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { parseAxiosError } from "@shared/components/utils/apiErrorParser";
import { getSubscriptionChain } from "./requests/subscriptionsApi";
import {
	formatSubscriptionDate,
	getStateBadge,
	SERVICE_TYPE_LABEL,
} from "./SubscriptionsData";
import type {
	Subscription,
	SubscriptionChainEntry,
	SubscriptionChainResponse,
	SubscriptionEvent,
} from "./types";

interface SubscriptionHistoryModalProps {
	subscription: Subscription | null;
	onClose: () => void;
}

/**
 * `id` → 1-based position in the chain.
 *
 * Every term of a chain reports the same `subscriptionId` (CTX-584), so naming
 * one by its id would name all of them. The audit metadata references terms by
 * row id, which means nothing to an operator — this turns those into the
 * position they can actually see in the timeline next to it.
 */
function buildTermPositions(
	chain: SubscriptionChainEntry[],
): Map<number, number> {
	return new Map(chain.map((entry, index) => [entry.id, index + 1]));
}

/** `term #2`, or null when the id is absent or points outside the chain. */
function termLabel(
	positions: Map<number, number>,
	id: unknown,
): string | null {
	const position = typeof id === "number" ? positions.get(id) : undefined;
	return position ? `term #${position}` : null;
}

/**
 * Human sentence for an audit row. The backend stores `eventType` plus a
 * free-form `metadata` bag; rather than dumping it, translate the shapes we
 * know and fall back to the raw type so a new event kind still renders
 * something intelligible instead of blank.
 */
function describeEvent(
	event: SubscriptionEvent,
	positions: Map<number, number>,
): string {
	const meta = event.metadata ?? {};
	const renewedTo = termLabel(positions, meta.renewedToRowId);
	const renewedFrom = termLabel(positions, meta.renewedFromRowId);
	const termMonths = typeof meta.termMonths === "number" ? meta.termMonths : null;

	switch (event.eventType) {
		case "created":
			return "Subscription created";
		case "renewed":
			return [
				"Renewed",
				termMonths ? `for ${termMonths} months` : null,
				renewedFrom ? `from ${renewedFrom}` : null,
			]
				.filter(Boolean)
				.join(" ");
		case "state_change":
			return [
				`${event.fromState ?? "—"} → ${event.toState ?? "—"}`,
				renewedTo ? `(renewed into ${renewedTo})` : null,
			]
				.filter(Boolean)
				.join(" ");
		default:
			return event.eventType;
	}
}

/** Timeline dot color, matched to the badge variant of the entry's state. */
const NODE_COLOR_BY_VARIANT: Record<string, string> = {
	success: "text-semantic-text-success",
	warning: "text-semantic-text-warning",
	error: "text-semantic-text-danger",
	info: "text-semantic-text-brand",
	neutral: "text-semantic-text-secondary",
};

const Fact: React.FC<{ label: string; children: React.ReactNode }> = ({
	label,
	children,
}) => (
	<div className="flex flex-col min-w-0">
		<span className="text-xs uppercase tracking-wide text-semantic-text-secondary">
			{label}
		</span>
		<span className="text-sm font-semibold mt-1 tabular-nums">{children}</span>
	</div>
);

/**
 * Full subscription history of a customer's service (CTX-26).
 *
 * A service can own many subscriptions over its lifetime — expired, current
 * and future — linked by `renewedFromId`. They all carry the SAME
 * `subscriptionId`, so the timeline identifies each one by its position in the
 * chain rather than by a label. This shows that chain
 * plus the audit trail behind it, which is the only place an operator can see
 * who renewed what and when.
 */
const SubscriptionHistoryModal: React.FC<SubscriptionHistoryModalProps> = ({
	subscription,
	onClose,
}) => {
	const api = useAxiosAuth0();
	const [data, setData] = useState<SubscriptionChainResponse | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const subscriptionId = subscription?.subscriptionId ?? null;

	// Recomputed only when the chain changes; both the timeline and the audit
	// trail name terms through it.
	const termPositions = useMemo(
		() => buildTermPositions(data?.chain ?? []),
		[data],
	);

	useEffect(() => {
		if (!subscriptionId) return undefined;
		let active = true;
		setLoading(true);
		setError(null);
		getSubscriptionChain(api, subscriptionId)
			.then((response) => {
				if (active) setData(response);
			})
			.catch((err) => {
				if (active) setError(parseAxiosError(err).message);
			})
			.finally(() => {
				if (active) setLoading(false);
			});
		return () => {
			active = false;
		};
	}, [api, subscriptionId]);

	if (!subscription) return null;

	return (
		<GenericModal
			isOpen
			onClose={onClose}
			size="lg"
			customWidth="max-w-4xl"
			title={
				<div className="flex flex-col min-w-0">
					<span className="text-h5 font-bold">Subscription history</span>
					<span className="text-sm font-normal text-semantic-text-secondary mt-0.5 truncate">
						{subscription.customerName} ·{" "}
						{SERVICE_TYPE_LABEL[subscription.serviceType]} {subscription.serviceName}
					</span>
				</div>
			}
			footer={
				<div className="flex justify-end w-full">
					<GenericButton label="Close" variant="secondary" size="md" onClick={onClose} />
				</div>
			}
		>
			{loading && <SkeletonTableRows rows={6} cols={3} rowHeight={28} animated />}

			{!loading && error && (
				<p className="rounded-sm border border-semantic-border-danger-primary bg-semantic-bg-danger-secondary px-4 py-3 text-sm text-semantic-text-danger">
					{error}
				</p>
			)}

			{!loading && !error && data && (
				<div className="flex flex-col gap-5">
					<div className="grid grid-cols-2 sm:grid-cols-3 gap-4 rounded-sm border border-semantic-border-primary bg-semantic-bg-secondary px-4 py-3">
						<Fact label="Subscriptions in chain">{data.chain.length}</Fact>
						<Fact label="Accumulated term">
							{data.accumulatedTermMonths} months
						</Fact>
						<Fact label="Covered until">
							{formatSubscriptionDate(data.effectiveEndDate)}
						</Fact>
					</div>

					<div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)] gap-6 items-start">
						<section>
							<h4 className="text-sm font-bold pb-2 mb-3 border-b border-semantic-border-primary">
								Renewal chain
								<span className="ml-2 font-normal text-semantic-text-secondary">
									{data.chain.length}{" "}
									{data.chain.length === 1 ? "subscription" : "subscriptions"}
								</span>
							</h4>
							<ol className="flex flex-col list-none p-0 m-0">
								{data.chain.map((entry, index) => {
									const badge = getStateBadge(entry.state);
									// By row id: the whole chain shares one subscriptionId, so
									// comparing labels marked every term as the current one.
									const isCurrent = entry.id === subscription.id;
									const isLast = index === data.chain.length - 1;
									return (
										<li
											key={entry.id}
											className="grid grid-cols-[20px_1fr] gap-3"
										>
											<div className="flex flex-col items-center">
												<span
													className={`w-3 h-3 rounded-full mt-2 bg-current ${
														NODE_COLOR_BY_VARIANT[badge.variant] ??
														"text-semantic-text-secondary"
													}`}
													aria-hidden
												/>
												{!isLast && (
													<span
														className="w-0.5 flex-1 my-1 bg-semantic-border-secondary"
														aria-hidden
													/>
												)}
											</div>
											<div
												className={`rounded-sm border px-3 py-2.5 mb-2.5 ${
													isCurrent
														? "border-semantic-border-brand-secondary bg-semantic-bg-brand-secondary"
														: "border-semantic-border-primary"
												}`}
											>
												<div className="flex flex-wrap items-center gap-2">
													<span className="font-semibold text-semantic-text-brand">
														{entry.subscriptionId}
													</span>
													<StatusBadge variant={badge.variant} label={badge.label} />
													{isCurrent && (
														<StatusBadge
															variant="info"
															label="Selected row"
															withIcon={false}
														/>
													)}
												</div>
												<div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 mt-2 text-sm text-semantic-text-secondary">
													<span>
														Term{" "}
														<b className="text-semantic-text-primary tabular-nums">
															{formatSubscriptionDate(entry.contractStart)} →{" "}
															{formatSubscriptionDate(entry.contractEnd)}
														</b>
													</span>
													<span>
														Length{" "}
														<b className="text-semantic-text-primary tabular-nums">
															{entry.billingCycle ?? "—"} months
														</b>
													</span>
													<span>
														Auto-renew{" "}
														<b className="text-semantic-text-primary">
															{entry.autoRenew ? "Yes" : "No"}
														</b>
													</span>
													<span>
														Renewed from{" "}
														<b className="text-semantic-text-primary">
															{termLabel(termPositions, entry.renewedFromId) ??
																"—"}
														</b>
													</span>
												</div>
											</div>
										</li>
									);
								})}
							</ol>
						</section>

						<section>
							<h4 className="text-sm font-bold pb-2 mb-3 border-b border-semantic-border-primary">
								Audit trail
							</h4>
							{data.events.length === 0 ? (
								<p className="text-sm text-semantic-text-secondary">
									No events recorded for this chain yet.
								</p>
							) : (
								<ul className="flex flex-col list-none p-0 m-0">
									{data.events.map((event) => (
										<li
											key={event.id}
											className="flex flex-col gap-0.5 py-2 border-b border-dashed border-semantic-border-primary last:border-b-0"
										>
											<div className="flex items-baseline gap-2">
												<span className="text-xs text-semantic-text-secondary tabular-nums whitespace-nowrap">
													{format(new Date(event.createdAt), "dd MMM yyyy HH:mm")}
												</span>
												<span className="ml-auto text-xs text-semantic-text-secondary whitespace-nowrap truncate">
													{event.actorLabel ?? "system"}
												</span>
											</div>
											<span className="text-sm">
												{describeEvent(event, termPositions)}
											</span>
											<span className="text-xs text-semantic-text-secondary">
												{termLabel(
													termPositions,
													event.metadata?.subscriptionRowId,
												) ?? event.subscriptionId}
											</span>
										</li>
									))}
								</ul>
							)}
						</section>
					</div>
				</div>
			)}
		</GenericModal>
	);
};

export default SubscriptionHistoryModal;
